import { EventType } from "@/analytics/analytics-events";
import { useCurrentScene } from "@/modes/mode-data-context";
import { selectActiveArea } from "@/store/selections-selectors";
import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import { setPdfReportSheetForArea } from "@/tools/create-report/pdf-report-slice";
import {
  cyan,
  FaroMenu,
  FaroMenuProps,
  FaroText,
  NoTranslate,
  SingleSelectItem,
  TruncatedFaroText,
} from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import {
  GUID,
  IElementGenericImgSheet,
  isIElementAreaSection,
  isIElementGenericDataSession,
  isIElementGenericDataset,
} from "@faro-lotv/ielement-types";
import { selectAncestor } from "@faro-lotv/project-source";
import CheckIcon from "@mui/icons-material/Check";
import Stack from "@mui/material/Stack";
import { useCallback, useMemo, type JSX } from "react";
import { getSheetIconComponent } from "./multi-sheet-menu-item";

export type PdfReportSheetMenuProps = Pick<
  FaroMenuProps,
  "open" | "anchorEl" | "onClose"
>;

/**
 * @returns A sheet selection menu specifically for PDF report mode.
 * Shows sheets with single-selection behavior (only one visible at a time),
 * a checkmark for the selected sheet, and dimmed non-selected sheets.
 */
export function PdfReportSheetMenu({
  open,
  anchorEl,
  onClose,
}: PdfReportSheetMenuProps): JSX.Element {
  const { availableSheets, activeSheets } = useCurrentScene();
  const activeArea = useAppSelector(selectActiveArea);
  const dispatch = useAppDispatch();

  const isSheetSelected = useCallback(
    (sheetId: GUID) => activeSheets.some((s) => s.id === sheetId),
    [activeSheets],
  );

  const sortedSheets = useMemo(
    () =>
      [...availableSheets].sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
      ),
    [availableSheets],
  );

  return (
    <FaroMenu
      open={open}
      anchorEl={anchorEl}
      anchorOrigin={{ vertical: "top", horizontal: -2 }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      onClose={onClose}
      dark
    >
      <Stack>
        <Stack sx={{ p: 1.5 }}>
          <FaroText variant="heading14" sx={{ mt: "1", mb: "1" }} dark>
            Sheets
          </FaroText>
        </Stack>

        <FaroText variant="helpText" sx={{ px: 1.5, pb: 0.75 }} dark>
          Select the sheets to include in the PDF report. <br />
          Only one sheet can be added at a time.
        </FaroText>

        {sortedSheets.map((sheet) => (
          <PdfReportSheetMenuItem
            key={sheet.id}
            sheet={sheet}
            isSelected={isSheetSelected(sheet.id)}
            onSheetSelected={(sheetId, areaId) => {
              Analytics.track(EventType.selectPDFReportSheet);

              dispatch(setPdfReportSheetForArea({ areaId, sheetId }));
            }}
          />
        ))}

        {!activeArea && sortedSheets.length === 0 && (
          <FaroText variant="bodyS" sx={{ px: 1.5, py: 1 }} dark>
            No sheets available
          </FaroText>
        )}
      </Stack>
    </FaroMenu>
  );
}

type PdfReportSheetMenuItemProps = {
  /** The sheet element */
  sheet: IElementGenericImgSheet;
  /** Whether this sheet is currently selected */
  isSelected: boolean;
  /** Callback when the sheet is selected */
  onSheetSelected(sheetId: GUID, areaId: GUID): void;
};

function PdfReportSheetMenuItem({
  sheet,
  isSelected,
  onSheetSelected,
}: PdfReportSheetMenuItemProps): JSX.Element {
  const reference = useAppSelector((state) =>
    selectAncestor(
      sheet,
      (el) =>
        isIElementAreaSection(el) ||
        isIElementGenericDataSession(el) ||
        isIElementGenericDataset(el),
    )(state),
  );

  const area = useAppSelector((state) =>
    selectAncestor(sheet, isIElementAreaSection)(state),
  );

  const Icon = getSheetIconComponent(reference);

  const selectedColor = isSelected ? cyan[500] : undefined;

  return (
    <SingleSelectItem
      selected={isSelected}
      disabled={false}
      onClick={() => area && onSheetSelected(sheet.id, area.id)}
      Icon={Icon}
      label={
        <TruncatedFaroText
          variant="bodyM"
          color={selectedColor ?? "inherit"}
          dark
        >
          <NoTranslate component="span">{sheet.name}</NoTranslate>
        </TruncatedFaroText>
      }
      extraAction={
        isSelected ? (
          <CheckIcon sx={{ fontSize: 18, color: selectedColor }} />
        ) : undefined
      }
      sx={isSelected ? { color: selectedColor } : undefined}
      value={sheet.id}
      dark
    />
  );
}
